/* eslint-disable no-underscore-dangle */
import { Q } from '@nozbe/watermelondb';
import { useIsFocused } from '@react-navigation/core';
import React, { useEffect, useState } from 'react';
import { Text, View } from 'react-native';
import DropdownSelect from 'react-native-input-select';

import { Header } from '@/components/Header';
import { SafeAreaView } from '@/components/SafeAreaView';
import { useSyncContext } from '@/contexts/syncContext';
import { database } from '@/database';
import type { FormModel } from '@/database/model/Form';
import type { InterviewModel } from '@/database/model/Interview';
import type { IRawInterview, ISyncList } from '@/libs/types/sync';
import { Color } from '@/styles/GlobalStyles';

import { Card } from './components/Card';
import { InterviewChart } from './components/InterviewChart';
import { Table } from './components/Table';
import { WebDatePicker } from './components/WebDatePicker';
import { mapInterviewStatus } from './helpers/interviewStatus';
import { styles } from './styles';

export function Dashboard() {
  const isFocused = useIsFocused();
  const { isSyncing } = useSyncContext();

  const [interviews, setInterviews] = useState<ISyncList[]>([]);
  const [filterStartDate, setFilterStartDate] = useState(() => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    return startOfMonth.toISOString().split('T')[0];
  });

  const [filterEndDate, setFilterEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });

  // Calculate min date for the picker (Start of current month)
  const minDate = React.useMemo(() => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth() - 2, 1);
    return startOfMonth.toISOString().split('T')[0];
  }, []);

  const [filterFormName, setFilterFormName] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    const getInterviews = async () => {
      const interviewsData = await database
        .get<InterviewModel>('interviews')
        .query(Q.sortBy('created_at', Q.desc));
      const data = await Promise.all(
        interviewsData.map(async (interview) => {
          const form = await database
            .get<FormModel>('forms')
            .query(
              Q.and(
                Q.where('form_id', interview.form_id),
                Q.where('form_version', interview.form_version),
              ),
            )
            .fetch();
          return {
            ...(interview._raw as IRawInterview),
            form_name: form[0]?.name,
          };
        }),
      );
      setInterviews(
        data.filter((interview) => {
          if (interview._status === 'synced') {
            return interview.interview_id !== 0;
          }
          return interview;
        }),
      );
    };

    getInterviews();
  }, [isFocused, isSyncing]);

  const filteredAndSortedInterviews = React.useMemo(() => {
    let result = [...interviews];

    // Filter by Date Range
    if (filterStartDate || filterEndDate) {
      result = result.filter((interview) => {
        const interviewDate = new Date(interview.created_at);
        interviewDate.setHours(0, 0, 0, 0);

        let isValid = true;

        if (filterStartDate) {
          const startDate = new Date(filterStartDate);
          startDate.setHours(0, 0, 0, 0);
          const [y, m, d] = filterStartDate.split('-').map(Number);
          const localStartDate = new Date(y, m - 1, d);
          if (interviewDate < localStartDate) isValid = false;
        }

        if (filterEndDate) {
          const [y, m, d] = filterEndDate.split('-').map(Number);
          const localEndDate = new Date(y, m - 1, d);
          if (interviewDate > localEndDate) isValid = false;
        }

        return isValid;
      });
    }

    // Filter by Form Name
    if (filterFormName) {
      result = result.filter((interview) =>
        interview.form_name
          ?.toLowerCase()
          .includes(filterFormName.toLowerCase()),
      );
    }

    // Filter by Status
    if (filterStatus) {
      result = result.filter(
        (interview) => mapInterviewStatus(interview) === filterStatus,
      );
    }

    // Sort
    if (sortConfig) {
      result.sort((a, b) => {
        let aValue: string | number = '';
        let bValue: string | number = '';

        if (sortConfig.key === 'mapped_status') {
          aValue = mapInterviewStatus(a);
          bValue = mapInterviewStatus(b);
        } else if (sortConfig.key === 'duration') {
          aValue = a.updated_at - a.created_at;
          bValue = b.updated_at - b.created_at;
        } else {
          // @ts-ignore
          aValue = a[sortConfig.key] ?? '';
          // @ts-ignore
          bValue = b[sortConfig.key] ?? '';
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return result;
  }, [
    interviews,
    filterStartDate,
    filterEndDate,
    filterFormName,
    filterStatus,
    sortConfig,
  ]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterStartDate, filterEndDate, filterFormName, filterStatus]);

  const paginatedInterviews = filteredAndSortedInterviews.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const handleSort = (key: string) => {
    setSortConfig((current) => {
      if (current?.key === key) {
        return {
          key,
          direction: current.direction === 'asc' ? 'desc' : 'asc',
        };
      }
      return { key, direction: 'asc' };
    });
  };

  const formNameOptions = React.useMemo(() => {
    const names = Array.from(
      new Set(interviews.map((i) => i.form_name)),
    ).filter(Boolean);
    return names.map((name) => ({ label: name, value: name }));
  }, [interviews]);

  const statusOptions = [
    { label: 'Válida', value: 'Válida' },
    { label: 'Não Considerada', value: 'Não Considerada' },
    { label: 'Descartada', value: 'Descartada' },
  ];

  return (
    <SafeAreaView>
      <Header />

      <View style={styles.container}>
        <View style={styles.header}>
          <View style={styles.infoScreen}>
            <Text style={styles.name}>Dashboard</Text>
            <Text style={styles.title}>Acompanhamento de entrevistas</Text>
          </View>
        </View>

        <View style={styles.dashboardContainer}>
          <View style={styles.filterWrapper}>
            <View style={styles.filterContainer}>
              {/* Date Range Group */}
              <View style={[styles.filterGroup, styles.filterItemDate]}>
                <View style={styles.dateRangeContainer}>
                  <View style={[styles.filterGroup, styles.dateField]}>
                    <Text style={styles.filterLabel}>DE</Text>
                    <WebDatePicker
                      style={styles.input}
                      value={filterStartDate}
                      onChange={setFilterStartDate}
                      min={minDate}
                    />
                  </View>
                  <View style={[styles.filterGroup, styles.dateField]}>
                    <Text style={styles.filterLabel}>ATÉ</Text>
                    <WebDatePicker
                      style={styles.input}
                      value={filterEndDate}
                      onChange={setFilterEndDate}
                      min={filterStartDate}
                    />
                  </View>
                </View>
              </View>

              {/* Form Name Filter */}
              <View style={[styles.filterGroup, styles.filterItemForm]}>
                <Text style={styles.filterLabel}>Questionário</Text>
                <DropdownSelect
                  placeholder="Selecione..."
                  options={formNameOptions}
                  selectedValue={filterFormName}
                  onValueChange={(value: any) => setFilterFormName(value)}
                  primaryColor={Color.primary[300]}
                  dropdownContainerStyle={{
                    marginBottom: 0,
                  }}
                  dropdownStyle={{
                    backgroundColor: Color.gray[50],
                    borderColor: Color.gray[100],
                    borderWidth: 1,
                    borderRadius: 8,
                    height: 64,
                    paddingVertical: 0,
                    justifyContent: 'center',
                    marginTop: 0,
                    marginBottom: 0,
                  }}
                  dropdownIconStyle={{ top: 22, right: 12 }}
                />
              </View>

              {/* Status Filter */}
              <View style={[styles.filterGroup, styles.filterItemStatus]}>
                <Text style={styles.filterLabel}>Status</Text>
                <DropdownSelect
                  placeholder="Selecione..."
                  options={statusOptions}
                  selectedValue={filterStatus}
                  onValueChange={(value: any) => setFilterStatus(value)}
                  primaryColor={Color.primary[300]}
                  dropdownContainerStyle={{
                    marginBottom: 0,
                  }}
                  dropdownStyle={{
                    backgroundColor: Color.gray[50],
                    borderColor: Color.gray[100],
                    borderWidth: 1,
                    borderRadius: 8,
                    height: 64,
                    paddingVertical: 0,
                    justifyContent: 'center',
                    marginTop: 0,
                    marginBottom: 0,
                  }}
                  dropdownIconStyle={{ top: 22, right: 12 }}
                />
              </View>
            </View>
          </View>
          <Card interviews={filteredAndSortedInterviews} />
          <InterviewChart interviews={filteredAndSortedInterviews} />
          <Table
            interviews={paginatedInterviews}
            onSort={handleSort}
            sortConfig={sortConfig}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            totalItems={filteredAndSortedInterviews.length}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={setItemsPerPage}
          />
        </View>
      </View>
    </SafeAreaView>
  );
}
